import React, { useState } from "react";
import { api, authHeader, errMessage, downloadAuthed } from "../../api";
import { fmtDayShort } from "../../lib/dates";

export default function SubscribersPanel({ rows, integrations, reload, loading }) {
  const [syncing, setSyncing] = useState(false);
  const [note, setNote] = useState("");

  const live = integrations?.newsletter?.configured;

  const resync = async () => {
    setSyncing(true);
    setNote("");
    try {
      const { data } = await api.post("/admin/subscribers/resync", null, {
        headers: authHeader(),
      });
      setNote(
        data.status === "done"
          ? `Sent ${data.synced} of ${data.total} to EmailOctopus.`
          : data.detail,
      );
      reload();
    } catch (e) {
      setNote(errMessage(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <section className="admin-sec" data-testid="admin-subscribers-panel">
      <header>
        <h2>The list</h2>
        {rows.length ? (
          <button
            type="button"
            className="act"
            onClick={() =>
              downloadAuthed("/admin/export/subscribers.csv", "schema-subscribers.csv")
            }
            data-testid="admin-subscribers-export-button"
          >
            Export (CSV)
          </button>
        ) : null}
      </header>
      <p className="admin-help">
        Everyone who asked to be written to when something is on. These are kept
        here on your own site first, so they&rsquo;re yours regardless of which
        newsletter service you use.{" "}
        {live ? (
          <>They&rsquo;re also being pushed to EmailOctopus automatically.</>
        ) : (
          <>
            EmailOctopus isn&rsquo;t connected yet, so the CSV above is how you
            move them across &mdash; it&rsquo;s already in the column order
            EmailOctopus expects.
          </>
        )}
      </p>

      {live ? (
        <div className="actions" style={{ marginTop: 0, marginBottom: "1.6rem" }}>
          <button
            type="button"
            className="act"
            onClick={resync}
            disabled={syncing}
            data-testid="admin-subscribers-resync-button"
          >
            {syncing ? "Sending\u2026" : "Push everyone to EmailOctopus"}
          </button>
        </div>
      ) : null}

      {note ? (
        <div className="notice ok" data-testid="admin-subscribers-note">
          {note}
        </div>
      ) : null}

      {loading ? (
        <p className="admin-help">Looking&hellip;</p>
      ) : rows.length === 0 ? (
        <div className="empty" data-testid="admin-subscribers-empty">
          <h3 style={{ marginBottom: ".8rem" }}>Nobody on the list yet.</h3>
          <p>
            The opt-in sits at the foot of the home page, the gatherings page and
            the circles page, and on the get-in-touch form.
          </p>
        </div>
      ) : (
        <>
          <table className="tbl" data-testid="admin-subscribers-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Came from</th>
                <th>Added</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} data-testid={`admin-subscriber-row-${s.id}`}>
                  <td>
                    <a href={`mailto:${s.email}`}>{s.email}</a>
                  </td>
                  <td>{s.name || "\u2014"}</td>
                  <td>{s.source || "\u2014"}</td>
                  <td className="num">{fmtDayShort(s.created_at)}</td>
                  <td>
                    <span
                      className={`chip ${
                        s.status === "subscribed" ? "chip--on" : "chip--off"
                      }`}
                    >
                      {s.status}
                    </span>
                    {s.provider_status && s.provider_status !== "not_configured" ? (
                      <>
                        {" "}
                        <span className="chip">{s.provider_status}</span>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="stack">
            {rows.map((s) => (
              <div className="rowcard" key={s.id}>
                <div className="rt">{s.email}</div>
                <dl>
                  {s.name ? (
                    <>
                      <dt>Name</dt>
                      <dd>{s.name}</dd>
                    </>
                  ) : null}
                  <dt>From</dt>
                  <dd>{s.source}</dd>
                  <dt>Added</dt>
                  <dd>{fmtDayShort(s.created_at)}</dd>
                  <dt>State</dt>
                  <dd>{s.status}</dd>
                </dl>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
